import React, { useRef } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseDatePicker from "../../../components/ui/BaseDatePicker";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { addUserQualifications, updateUserQualifications } from "../../../services/api/user";
import { fetchSubQualificationTypes } from "../../../services/api/qualification";
import { validateUploadSize } from "../../../utils/helpers";

const getValidationSchema = (isEdit) =>
  yup.object({
    file: isEdit
      ? yup.mixed().nullable()
      : yup
          .mixed()
          .required("File is required")
          .test("fileSize", "File size is too large", (value, ctx) =>
            validateUploadSize(value, ctx),
          )
          .test(
            "fileType",
            "Unsupported file format (PDF, JPG, PNG, WEBP allowed)",
            (value) =>
              value &&
              ["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(value.type),
          ),
    // Only the insurance type + file are collected in this modal now.
    qualificationSubTypeId: yup.string("").required("Choose the insurance type"),
    companyName: yup.string().notRequired(),
    policyNumber: yup.string().notRequired(),
    coveragePerIncident: yup.string().notRequired(),
    startDate: yup.string().notRequired(),
    endDate: yup.string().notRequired(),
  });

const AddGeneralInsuranceModal = ({
  open,
  close,
  selectedInsurance,
  setSelectedInsurance,
  insuranceId,
  qualificationSubTypeId,
  refetchUserQualificationsByType,
  targetUserId,
}) => {
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const isInsuranceEdit = selectedInsurance ? true : false;

  const {
    isPending: isUserInsuranceLoading,
    error: isUserInsuranceError,
    data: userInsuranceData,
    mutateAsync: addUserQualificationsMutation,
  } = useMutation({
    mutationFn: ({ formData, targetUserId: target }) => addUserQualifications(formData, target),
    enabled: false,
  });

  const {
    isPending: isUpdateUserQualificationsApiLoading,
    error: isUpdateUserQualificationsApiError,
    data: updatedUserQualificationData,
    mutateAsync: updateUserQualificationsMutation,
  } = useMutation({
    mutationFn: updateUserQualifications,
    enabled: false,
  });

  const {
    isLoading: isSubQualificationsLoading,
    error: isSubQualificationsError,
    data: subQualificationsData,
  } = useQuery({
    queryKey: ["subQualifications", insuranceId],
    queryFn: () => fetchSubQualificationTypes(insuranceId),
    enabled: !!insuranceId,
  });

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const formik = useFormik({
    initialValues: {
      file: null,
      companyName: selectedInsurance?.companyName,
      policyNumber: selectedInsurance?.policyNumber,
      startDate: selectedInsurance?.startDate || "",
      endDate: selectedInsurance?.endDate || "",
      coveragePerIncident: selectedInsurance?.coveragePerIncident,
      qualificationSubTypeId: qualificationSubTypeId || selectedInsurance?.qualificationSubTypeId,
    },
    validationSchema: getValidationSchema(isInsuranceEdit),
    onSubmit: (values, { resetForm }) => {
      handleInsuranceSubmit(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleFileChange = (event) => {
    const file = event.target?.files[0];
    formik.setFieldValue("file", file);
  };

  // Remove the selected (not-yet-saved) file so the user can re-choose.
  const handleRemoveFile = () => {
    formik.setFieldValue("file", null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleInsuranceSubmit = (values) => {
    if (isInsuranceEdit) {
      handleEditInsurance();
    } else {
      handleAddInsurance(values);
    }
  };

  const handleAddInsurance = async (values) => {
    try {
      const formData = new FormData();
      formData.append("file", values.file);
      Object.keys(values).forEach((key) => {
        // Skip the file (already appended) and any empty/undefined fields so we
        // don't send literal "undefined" strings to the API.
        if (
          key !== "file" &&
          values[key] !== undefined &&
          values[key] !== null &&
          values[key] !== ""
        ) {
          formData.append(key, values[key]);
        }
      });

      await addUserQualificationsMutation({ formData, targetUserId });
      dispatch(
        showToast({
          message: "Insurance added successfully",
          type: "success",
        }),
      );
      close(false);

      refetchUserQualificationsByType();
      formik.resetForm();
    } catch (error) {
      dispatch(showToast({ message: error?.response?.data?.message, type: "error" }));
    }
  };

  const handleEditInsurance = async () => {
    try {
      const { file, ...rest } = formik.values;

      const formData = new FormData();
      for (const key in rest) {
        if (rest[key] !== undefined && rest[key] !== null) {
          formData.append(key, rest[key]);
        }
      }

      if (file) {
        formData.append("file", file);
      }

      const insuranceResponse = await updateUserQualificationsMutation({
        id: selectedInsurance.id,
        data: formData,
        targetUserId,
      });
      refetchUserQualificationsByType();
      dispatch(
        showToast({
          message: "Insurance updated successfully",
          type: "success",
        }),
      );
      formik.resetForm();
      setSelectedInsurance?.({});
      close(false);
    } catch (error) {
      console.log(error);
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  console.log("🚀 ~ AddGeneralInsuranceModal ~ subQualificationsData:", subQualificationsData);
  return (
    <form onSubmit={formik.handleSubmit}>
      <BaseModal
        className="-top-[200px] overflow-auto"
        open={open}
        close={() => {
          close(false);
          formik.resetForm();
          setSelectedInsurance?.({});
        }}
      >
        <div className="py-7 px-5 sm:px-12 w-full lg:w-[900px] xl:w-[1000px]">
          <h6 className="text-dark font-normal border-b pb-1">Upload Insurance</h6>
          <div className="my-3">
            <p className="font-normal text-base mb-2">Insurance Type</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-14 gap-y-2">
              {subQualificationsData?.data.map((subQualification) => (
                <BaseCheckbox
                  label={subQualification.name}
                  id={subQualification.id}
                  name={subQualification.name}
                  checked={formik.values.qualificationSubTypeId === subQualification.id}
                  onChange={() => {
                    formik.setFieldValue("qualificationSubTypeId", subQualification.id);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.qualificationSubTypeId &&
                    Boolean(formik.errors.qualificationSubTypeId)
                  }
                ></BaseCheckbox>
              ))}
            </div>
            {formik.touched.qualificationSubTypeId && formik.errors.qualificationSubTypeId && (
              <p className="text-red-500 mt-1">{formik.errors.qualificationSubTypeId}</p>
            )}
          </div>

          {/* File upload is shown after the insurance type selection. */}
          <div className="mt-8 mb-3">
            <p className="font-normal text-base mb-2">
              Add File <span className="font-300 text-sm">(JPG, PDF, PNG)</span>
            </p>
            <div className="w-full flex flex-wrap items-center gap-3">
              {/* Hidden File Input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="file"
                name="file"
                accept=".pdf,.jpg,.png,.webp"
                onBlur={formik.handleBlur}
              />

              {/* File Display Section */}
              <div
                style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
                className="min-h-[40px] flex-1 min-w-[180px] py-[6.25px] rounded-md flex flex-wrap items-center"
              >
                {formik.values?.file ? (
                  // Selected (not-yet-saved) file rendered as a chip: clickable to
                  // preview, with a cross to remove.
                  <div className="ml-2 my-[2px] inline-flex items-center gap-2 bg-[#E9E9EB] rounded-[4px] py-1 pl-4 pr-2 max-w-full">
                    <a
                      href={URL.createObjectURL(formik.values.file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-pointer text-blue-500 hover:underline font-300 truncate"
                    >
                      {formik.values?.file?.name}
                    </a>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="flex items-center justify-center h-4 w-4 rounded-full text-gray-600 hover:bg-gray-300 hover:text-gray-800"
                      title="Remove file"
                    >
                      <i className="fa-solid fa-xmark text-xs"></i>
                    </button>
                  </div>
                ) : selectedInsurance?.digitalAsset?.url ? (
                  <a
                    href={selectedInsurance.digitalAsset.url} // Directly use existing file URL
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-1 px-6 cursor-pointer block text-blue-500 hover:underline font-300"
                  >
                    {selectedInsurance?.digitalAsset?.fileName || "Download File"}
                  </a>
                ) : (
                  <p className="text-gray-400 py-1 ml-2">No file selected</p>
                )}
              </div>

              {/* Choose File Button */}
              <BaseButton
                variant="outlined"
                color="primary"
                className="py-[7px] px-5 shrink-0 whitespace-nowrap"
                onClick={handleButtonClick}
              >
                Choose File
              </BaseButton>
            </div>
            {formik.touched.file && formik.errors.file && (
              <p className="text-red-500 mt-1">{formik.errors.file}</p>
            )}
          </div>
        </div>
        <div
          className="d-flex content-center  bg-white py-6 "
          style={{
            boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
          }}
        >
          <BaseButton
            className=" px-[28px] py-[5px] bg-light text-primary hover:text-primary"
            variant="outlined"
            color="primary"
            style={{
              marginRight: "16px",
            }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            isLoading={isUserInsuranceLoading || isUpdateUserQualificationsApiLoading}
            loaderSize={"22px"}
            loaderColor="primary"
            className="px-[12px] py-[5px]"
            color="primary"
            type="submit"
            // BaseModal portals content out of the <form>, so a bare type="submit"
            // never fires onSubmit — trigger the handler explicitly.
            onClick={formik.handleSubmit}
            // Save stays disabled until an insurance type is selected and a file
            // is attached (file not re-required when editing an existing policy).
            disabled={
              isUserInsuranceLoading ||
              isUpdateUserQualificationsApiLoading ||
              !formik.values.qualificationSubTypeId ||
              (!isInsuranceEdit && !formik.values.file)
            }
          >
            {isInsuranceEdit ? "Update" : "Save Changes"}
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
};

export default AddGeneralInsuranceModal;
